// ─── Film wall layout ─────────────────────────────────────────────────────────
// Packs films into justified rows (every row is scaled to exactly the wall width)
// and keeps drawing from the list until the wall is tall enough to pan around, so
// the mosaic never shows an empty edge — the wall is deliberately larger than
// the viewport. Repeats are marked `clone` so only the first copy of each film
// is focusable / announced.
//
// Order radiates from the centre: the first row built sits in the middle of the
// wall and the first films of each row sit in the middle of that row, so the top
// of films.json is what the screen opens on. Repeats are drawn in a shuffled
// order and never share a row, or a neighbouring row, with the same film.
// ─────────────────────────────────────────────────────────────────────────────

import { mulberry32 } from './seededRandom.js';

const RHYTHM = [1, 0.84, 1.12, 0.92];

export function filmRatio(film) {
  return film.width && film.height ? film.width / film.height : 16 / 9;
}

// Lays a list out from the middle: item 0 in the centre, each next item on the
// lighter side (ties go right), so [a, b, c, d, e] -> [e, c, a, b, d].
// With a `size`, items 1 and 2 keep their priority either side of item 0 and
// the rest are split between the sides as evenly as possible (every split is
// tried — rows hold a handful of tiles), so item 0 genuinely covers the centre
// even when it is a narrow vertical beside widescreen films.
export function centreOut(list, size) {
  if (!list.length) return [];
  const weight = size ?? (() => 1);
  const left = [], right = [];
  let l = 0, r = 0;
  const put = (item, toLeft) => {
    if (toLeft) { left.push(item); l += weight(item); } else { right.push(item); r += weight(item); }
  };

  const priority = size ? 3 : list.length;
  list.slice(1, priority).forEach((item) => put(item, l < r));

  const tail = list.slice(priority);
  if (tail.length <= 14) {
    const w = tail.map(weight);
    let best = 0, bestGap = Infinity;
    for (let mask = 0; mask < 1 << tail.length; mask++) {
      let dl = l, dr = r;
      w.forEach((x, i) => { if ((mask >> i) & 1) dl += x; else dr += x; });
      const gap = Math.abs(dl - dr);
      if (gap < bestGap - 1e-9) { bestGap = gap; best = mask; }
    }
    tail.forEach((item, i) => put(item, (best >> i) & 1));
  } else {
    tail.sort((a, b) => weight(b) - weight(a)).forEach((item) => put(item, l < r));
  }

  return [...left.reverse(), list[0], ...right];
}

function seededShuffle(list, seed) {
  const rand = mulberry32(seed);
  const out = list.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

// Wall size and tile scale for a viewport. The wall is wider and taller than the
// screen so the cursor has something to steer around; rowHeight sets how big the
// tiles read — at 1440x900 that is about 2.5 rows on screen.
export function wallMetrics(vw, vh) {
  return {
    width: vw * 1.34,
    height: vh * 1.4,
    rowHeight: Math.min(Math.max(vh * 0.4, 240), 460),
    gap: 6,
  };
}

export function layoutWall(films, { width, height, rowHeight, gap = 6, seed = 7 }) {
  if (!films.length || width <= 0) return { items: [], width, height: 0 };

  const n = films.length;
  const queue = [];
  let pass = 0;
  const refill = () => {
    queue.push(...(pass === 0 ? films : seededShuffle(films, seed + pass)));
    pass++;
  };

  // Rows are built in order r0, r1, r2… and stacked r3 r1 r0 r2 r4, so row k
  // touches row k-2 — and rows 1 and 2 both touch row 0.
  const rows = [];
  const neighbours = (k) => [k - 2, k === 1 || k === 2 ? 0 : -1]
    .filter((i) => i >= 0 && i !== k).flatMap((i) => rows[i].films.map((f) => f.id));

  let placed = 0;
  let total = 0;
  // An odd count leaves the same number of rows above and below row 0.
  while (total < height || placed < n || rows.length % 2 === 0) {
    const k = rows.length;
    const target = rowHeight * RHYTHM[k % RHYTHM.length];
    const blocked = new Set(neighbours(k));
    const row = [];
    let natural = 0;

    while (natural < width) {
      if (queue.length < n) refill();
      const inRow = new Set(row.map((f) => f.id));
      let i = queue.findIndex((f) => !inRow.has(f.id) && !blocked.has(f.id));
      if (i < 0) i = queue.findIndex((f) => !inRow.has(f.id));
      if (i < 0) i = 0;
      const [film] = queue.splice(i, 1);
      row.push(film);
      natural += filmRatio(film) * target + (row.length > 1 ? gap : 0);
    }

    // Overshot by more than the last tile was worth? Hand it back.
    const last = row[row.length - 1];
    const without = natural - filmRatio(last) * target - gap;
    if (row.length > 1 && width - without < natural - width) {
      row.pop();
      queue.unshift(last);
    }

    const ratios = row.map(filmRatio);
    const h = (width - gap * (row.length - 1)) / ratios.reduce((a, r) => a + r, 0);
    rows.push({ films: row, h });
    placed += row.length;
    total += h + gap;
    if (rows.length > 200) break;   // a degenerate width must not spin forever
  }

  // Stack centre-out, place left to right with each row's lead films in the middle.
  const items = [];
  const seen = new Map();
  let y = 0;
  centreOut(rows.map((r, k) => ({ ...r, k }))).forEach((r) => {
    let x = 0;
    centreOut(r.films, (f) => filmRatio(f) + gap / r.h).forEach((film) => {
      const w = filmRatio(film) * r.h;
      items.push({ film, x, y, w, h: r.h, row: r.k });
      x += w + gap;
    });
    y += r.h + gap;
  });

  // The copy nearest the middle of the wall is the "real" one.
  const cx = width / 2, cy = (y - gap) / 2;
  const dist = (it) => Math.hypot(it.x + it.w / 2 - cx, it.y + it.h / 2 - cy);
  items
    .map((it, i) => [dist(it), i])
    .sort((a, b) => a[0] - b[0])
    .forEach(([, i]) => {
      const it = items[i];
      const copy = seen.get(it.film.id) ?? 0;
      seen.set(it.film.id, copy + 1);
      it.key = `${it.film.id}~${copy}`;
      it.clone = copy > 0;
    });

  const lead = items.find((it) => !it.clone && it.film.id === films[0].id);
  return { items, width, height: y - gap, lead };
}

// ─── Steering ─────────────────────────────────────────────────────────────────
// The pan offset (how far the wall is shifted left / up) that puts the lead film
// in the middle of the screen. Row balancing gets it close to the wall's centre;
// this makes it exact even when a row can't be balanced around a narrow tile.
export function panAnchor(layout, vw, vh) {
  const rx = Math.max(layout.width - vw, 0);
  const ry = Math.max(layout.height - vh, 0);
  const it = layout.lead;
  if (!it) return { x: rx / 2, y: ry / 2, rx, ry };
  const clamp = (v, max) => Math.min(Math.max(v, 0), max);
  return { x: clamp(it.x + it.w / 2 - vw / 2, rx), y: clamp(it.y + it.h / 2 - vh / 2, ry), rx, ry };
}

// Cursor position (0…1 across the screen) -> pan offset. Piecewise so the middle
// of the screen maps to the anchor and the edges still reach the wall's edges.
export function steer(t, anchor, range) {
  return t <= 0.5 ? (t / 0.5) * anchor : anchor + ((t - 0.5) / 0.5) * (range - anchor);
}

// ─── Curvature ────────────────────────────────────────────────────────────────
// The wall bows toward the viewer like a lens: tiles recede and turn away the
// further they sit from the centre of the screen. nx / ny are the tile centre's
// offset from the viewport centre, normalised to -1…1 at the edges.
export function bulge(nx, ny, { depth = 190, tilt = 10 } = {}) {
  const z = -(nx * nx * 0.7 + ny * ny) * depth;
  return `translate3d(0,0,${z.toFixed(1)}px) rotateX(${(ny * tilt).toFixed(2)}deg) rotateY(${(-nx * tilt).toFixed(2)}deg)`;
}

export function formatRuntime(seconds) {
  if (seconds == null) return null;
  const s = Math.round(seconds);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}
